import { CoachProfileHeader } from '@efdigital/ll-addon-coaching';
import { Heading } from '@efdigital/ll-addon-ui';
import { Spinner } from '@efdigital/ll-xceed-v2/components/application/spinner.gts';
import SessionBookingFlow from '@efdigital/ll-xceed-v2/components/coaching/coachee/session-booking-flow.gts';
import type { CoachingCoacheeCohortSessionsBookChemistryModel } from '@efdigital/ll-xceed-v2/routes/coaching/coachee/cohort/sessions/book-session';
import { PAGE_WRAPPER_CONTENT } from '@efdigital/ll-xceed-v2/utils/layout';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { pageTitle } from 'ember-page-title';

interface BookSessionSignature {
  Args: {
    model: CoachingCoacheeCohortSessionsBookChemistryModel;
    controller: unknown;
  };
}

export default class BookSessionTemplate extends Component<BookSessionSignature> {
  @service declare router: RouterService;
  @tracked saving = false;

  bookSession = async (
    start: string,
    end: string,
    notes: string | undefined
  ) => {
    const idempotencyKey = crypto.randomUUID();
    this.saving = true;
    try {
      await this.args.model.planner.bookSession(
        this.args.model.coach.user,
        'coaching',
        idempotencyKey,
        start,
        end,
        notes
      );
      this.router.transitionTo(
        'coaching.coachee.cohort.sessions',
        this.args.model.program_id,
        this.args.model.cohort_id
      );
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        window.alert(error.message);
      }
    } finally {
      this.saving = false;
    }
  };

  <template>
    {{pageTitle "Book coaching session"}}

    <div class={{PAGE_WRAPPER_CONTENT}}>
      <CoachProfileHeader
        @coach={{@model.coach}}
        @partnershipStatus={{@model.planner.getStatusForCoach
          @model.coach.user
        }}
        class="mb-5"
      />

      {{#if (@model.planner.canBookCoachingSession @model.coach.user)}}
        <Heading
          @size="sm"
          @text="Book coaching session"
          @secondary="Enter notes, then select a time and date for your chemistry session."
        />
        <div class="card rounded-4 shadow-sm border p-3 p-lg-4">
          <div class="card-body">
            {{#if this.saving}}
              <Spinner />
            {{else}}
              <SessionBookingFlow
                @coachId={{@model.coach.user}}
                @coachingConfig={{@model.coachingConfig}}
                @sessionLengthMins={{@model.coachingConfig.defaultSessionDurationMinutes}}
                @allowedDurations={{@model.coachingConfig.allowedSessionDurations}}
                @onSlotSelected={{this.bookSession}}
              />
            {{/if}}
          </div>
        </div>
      {{else}}
        <div class="alert alert-danger" role="alert">
          <p class="mb-0">Sorry, you cannot book a coaching session with this
            coach.</p>
        </div>
      {{/if}}
    </div>
  </template>
}
